import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    TextField, Button, Stack, Typography, Alert, Box, Autocomplete, CircularProgress,
    MenuItem, FormControlLabel, Checkbox
} from '@mui/material';
import { crearTramiteAmparo, getTramitesAmparo, getSubtiposAmparo } from '../../services/amparoService';
import { buscarClientes, getClientePorCuil } from '../../services/clienteService';
import ModalRegistrarCliente from '../ModalRegistrarCliente';
import BotonGenerarEscritoAmparo from './BotonGenerarEscritoAmparo';
import SeccionGenerarPacto from '../Pacto/SeccionGenerarPacto';
import usePactosPendientes from '../Pacto/usePactosPendientes';
import useEscritoPendiente from '../Documentos/useEscritoPendiente';
import { generarEscritoAmparoTramite } from '../../services/amparoEscritoService';
import { useAuth } from '../../auth/AuthContext';
import { apellidoNombre } from '../../utils/nombrePersona';
import CampoFechaLiquidar from '../Tramite/CampoFechaLiquidar';
import { esDniOCuilValido, mensajeDniOCuilInvalido } from '../../utils/documentoUtils';
import PageHeader from '../ui/PageHeader';

// Respaldo del selector de subtipos: el backend los sirve en GET /tramites/amparo/subtipos (fuente
// de verdad, espejo del enum SubtipoAmparo), pero si esa llamada falla el formulario tiene que
// poder usarse igual — es un campo OBLIGATORIO, sin él no se puede dar de alta nada.
const SUBTIPOS_RESPALDO = [
    { valor: 'RTI', etiqueta: 'Jubilación por invalidez' },
    { valor: 'JUBILACION', etiqueta: 'Jubilación ordinaria' },
    { valor: 'PENSION_DIRECTA', etiqueta: 'Pensión directa' },
    { valor: 'PENSION_DERIVADA', etiqueta: 'Pensión derivada' },
    { valor: 'PNC', etiqueta: 'Pensión no contributiva' },
    { valor: 'EXPEDIENTE_357', etiqueta: 'Expediente 357' },
    { valor: 'REAJUSTE', etiqueta: 'Reajuste de haberes' },
];

// Los tipos de trámite que pueden ser el FONDO de un amparo: los previsionales que se presentan en
// ANSES. Se excluyen los dos amparos (no se ampara un amparo), ART y los judiciales.
const TIPOS_FONDO_VALIDOS = ['RTI', 'JUBILACION', 'PENSION', 'PNC', 'PUAM', 'REAJUSTE', 'RECONOCIMIENTO', 'PLAN_PAGO', 'GENERICO'];

const esTramiteDeFondoValido = (t) => {
    const nombre = String(t?.tipoTramite?.nombre || '').trim().toUpperCase();
    if (nombre.includes('AMPARO')) return false;
    return TIPOS_FONDO_VALIDOS.some((tipo) => nombre === tipo || nombre.startsWith(tipo));
};

/**
 * Formulario para iniciar un trámite de Amparo por mora ante ANSES (art. 28 Ley 19.549).
 *
 * El dato que lo define es el SUBTIPO: por qué trámite previsional se ampara. Es obligatorio (NOT
 * NULL en la base) porque sin él el escrito sale con el renglón del tipo de trámite en blanco.
 *
 * Opcionalmente se vincula al trámite de fondo del cliente: si se elige, el backend prellena el
 * número de expediente y la fecha de ingreso a ANSES desde la presentación del turno ANSES de ese
 * trámite, y la ficha del fondo muestra "Amparo iniciado".
 */
export default function FormularioIniciarTramiteAmparo({ onSuccess, onCancel, cuilInicial, idTurnoOrigen }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { cuil: cuilParam } = useParams();
    const cuilEfectivo = cuilInicial || cuilParam;

    const [form, setForm] = useState({
        idCliente: '',
        subtipo: '',
        idTramiteFondo: '',
        nroExpedienteAnses: '',
        udai: '',
        fechaInicioTramiteAnses: '',
        reclamaPorTercero: false,
        titularNombre: '',
        titularCuil: '',
        vinculoTitular: '',
        tiempoComprometido: '',
        fechaInicioEstimada: '',
        idUsuario: user?.id || null,
        etiqueta: '',
        comentarios: '',
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [guardando, setGuardando] = useState(false);

    const [subtipos, setSubtipos] = useState(SUBTIPOS_RESPALDO);

    // --- Búsqueda de cliente ---
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    // El PACTO y el escrito se emiten en este formulario, cuando el trámite todavía no existe:
    // los dos hooks los adjuntan recién cuando hay idTramite (ver usePactosPendientes).
    const { registrarPacto, adjuntarATramite: adjuntarPactos } = usePactosPendientes();
    const { registrarEscrito, adjuntarATramite: adjuntarEscrito } = useEscritoPendiente(generarEscritoAmparoTramite);
    const [opcionesCliente, setOpcionesCliente] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [modalClienteOpen, setModalClienteOpen] = useState(false);
    const busquedaTimeoutRef = useRef(null);

    // --- Trámites del cliente, para elegir el de fondo ---
    const [tramitesCliente, setTramitesCliente] = useState([]);
    const [cargandoTramites, setCargandoTramites] = useState(false);

    // --- Amparo duplicado para el mismo cliente ---
    const [tieneDuplicado, setTieneDuplicado] = useState(false);
    const [cantidadDuplicados, setCantidadDuplicados] = useState(0);
    const [cargandoDuplicados, setCargandoDuplicados] = useState(false);
    const [confirmarDuplicado, setConfirmarDuplicado] = useState(false);

    // Catálogo de subtipos del backend (si falla, se queda el respaldo de arriba).
    useEffect(() => {
        getSubtiposAmparo()
            .then((lista) => {
                if (Array.isArray(lista) && lista.length > 0) setSubtipos(lista);
            })
            .catch((err) => console.error('Error al cargar los subtipos de amparo:', err));
    }, []);

    // Carga inicial de cliente si viene por URL
    useEffect(() => {
        const cargar = async () => {
            if (!cuilEfectivo) return;
            try {
                const cliente = await getClientePorCuil(cuilEfectivo);
                setClienteSeleccionado(cliente);
                setForm(prev => ({ ...prev, idCliente: cliente.cuil }));
            } catch (err) {
                console.error('Error al cargar cliente inicial:', err);
            }
        };
        cargar();
    }, [cuilEfectivo]);

    // Trámites del cliente seleccionado, para el selector del trámite de fondo. Se piden por CUIL
    // (y no se reusa `clienteSeleccionado.tramites`) porque el objeto del autocompletado viene de
    // buscarClientes, que no los trae.
    useEffect(() => {
        const cuilCliente = form.idCliente;
        if (!cuilCliente) {
            setTramitesCliente([]);
            return;
        }
        let activo = true;
        setCargandoTramites(true);
        getClientePorCuil(cuilCliente)
            .then((cliente) => {
                if (!activo) return;
                const tramites = Array.isArray(cliente?.tramites) ? cliente.tramites : [];
                setTramitesCliente(tramites.filter(esTramiteDeFondoValido));
            })
            .catch((err) => {
                console.error('Error al cargar los trámites del cliente:', err);
                if (activo) setTramitesCliente([]);
            })
            .finally(() => { if (activo) setCargandoTramites(false); });
        return () => { activo = false; };
    }, [form.idCliente]);

    // Amparo ya existente para el cliente seleccionado
    useEffect(() => {
        const cuilCliente = form.idCliente;
        if (!cuilCliente) {
            setTieneDuplicado(false);
            setCantidadDuplicados(0);
            setConfirmarDuplicado(false);
            return;
        }
        let activo = true;
        const verificarDuplicado = async () => {
            setCargandoDuplicados(true);
            try {
                const tramites = await getTramitesAmparo(cuilCliente);
                const items = Array.isArray(tramites) ? tramites : [];
                if (activo) {
                    setCantidadDuplicados(items.length);
                    setTieneDuplicado(items.length > 0);
                    if (items.length === 0) setConfirmarDuplicado(false);
                }
            } catch (err) {
                console.error('Error validando amparos duplicados:', err);
                if (activo) { setTieneDuplicado(false); setCantidadDuplicados(0); }
            } finally {
                if (activo) setCargandoDuplicados(false);
            }
        };
        verificarDuplicado();
        return () => { activo = false; };
    }, [form.idCliente]);

    const handleBusquedaCliente = (_event, value) => {
        if (!value) { setOpcionesCliente([]); setBuscando(false); return; }
        setBuscando(true);
        if (busquedaTimeoutRef.current) clearTimeout(busquedaTimeoutRef.current);
        busquedaTimeoutRef.current = setTimeout(async () => {
            try {
                const resultados = await buscarClientes(value);
                setOpcionesCliente(resultados);
            } catch (err) {
                console.error('Error al buscar clientes', err);
            } finally {
                setBuscando(false);
            }
        }, 300);
    };

    const handleSeleccionCliente = (_event, newValue) => {
        setClienteSeleccionado(newValue);
        // Cambiar de cliente invalida el trámite de fondo elegido: es de otra persona.
        setForm(prev => ({ ...prev, idCliente: newValue ? newValue.cuil : '', idTramiteFondo: '' }));
    };

    const handleRegistroExitoso = (clienteNuevo) => {
        setClienteSeleccionado(clienteNuevo);
        setForm(prev => ({ ...prev, idCliente: clienteNuevo.cuil, idTramiteFondo: '' }));
        setModalClienteOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    // Elegir el trámite de fondo sugiere el subtipo, si todavía no se eligió ninguno: el amparo de
    // una jubilación es, casi siempre, "jubilación ordinaria". Es solo una sugerencia, se puede
    // cambiar (y no se pisa un subtipo ya elegido a mano).
    const handleSeleccionFondo = useCallback((valor) => {
        setForm(prev => {
            if (!valor) return { ...prev, idTramiteFondo: '' };
            const tramite = tramitesCliente.find(t => String(t.idTramite) === String(valor));
            const subtipoSugerido = prev.subtipo || sugerirSubtipo(tramite);
            return { ...prev, idTramiteFondo: valor, subtipo: subtipoSugerido };
        });
    }, [tramitesCliente]);

    const etiquetaTramite = (t) => {
        const nombre = String(t?.tipoTramite?.nombre || 'Trámite');
        const subtipo = t?.subtipoTramite ? ` · ${String(t.subtipoTramite).replace(/_/g, ' ').toLowerCase()}` : '';
        const estado = t?.nombreEstadoActual ? ` — ${t.nombreEstadoActual}` : '';
        const etiqueta = t?.etiqueta ? ` (${t.etiqueta})` : '';
        return `#${t?.idTramite} ${nombre}${subtipo}${estado}${etiqueta}`;
    };

    // Los datos que el escrito necesita, en el modo "alta" (el trámite todavía no existe).
    const datosEscrito = {
        cuil: form.idCliente,
        subtipo: form.subtipo || null,
        nroExpedienteAnses: form.nroExpedienteAnses || null,
        fechaInicioTramiteAnses: form.fechaInicioTramiteAnses || null,
        tiempoComprometido: form.tiempoComprometido || null,
        reclamaPorTercero: form.reclamaPorTercero,
        titularNombre: form.titularNombre || null,
        titularCuil: form.titularCuil || null,
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.idCliente) { setError('Debe seleccionar un cliente.'); return; }
        if (!form.subtipo) { setError('Debe indicar por qué trámite se ampara.'); return; }
        if (form.reclamaPorTercero && !form.titularNombre.trim()) {
            setError('Si el cliente reclama por un tercero, debe indicar el nombre del titular del beneficio.');
            return;
        }
        if (form.titularCuil && !esDniOCuilValido(form.titularCuil)) {
            setError(mensajeDniOCuilInvalido('El DNI/CUIL del titular'));
            return;
        }
        if (tieneDuplicado && !confirmarDuplicado) {
            setError('Debe confirmar que desea crear otro amparo por mora para un cliente que ya tiene uno.');
            return;
        }
        setError('');
        setSuccess('');
        setGuardando(true);
        try {
            const payload = {
                idCliente: form.idCliente,
                idUsuario: form.idUsuario,
                etiqueta: form.etiqueta || null,
                comentarios: form.comentarios,
                fechaInicioEstimada: form.fechaInicioEstimada || null,
                idTurnoOrigen: idTurnoOrigen || null,
                subtipo: form.subtipo,
                idTramiteFondo: form.idTramiteFondo ? Number(form.idTramiteFondo) : null,
                nroExpedienteAnses: form.nroExpedienteAnses || null,
                udai: form.udai || null,
                fechaInicioTramiteAnses: form.fechaInicioTramiteAnses || null,
                reclamaPorTercero: form.reclamaPorTercero,
                titularNombre: form.reclamaPorTercero ? (form.titularNombre || null) : null,
                titularCuil: form.reclamaPorTercero ? (form.titularCuil || null) : null,
                vinculoTitular: form.reclamaPorTercero ? (form.vinculoTitular || null) : null,
                tiempoComprometido: form.tiempoComprometido || null,
            };
            const creado = await crearTramiteAmparo(payload);
            // El pacto y el escrito se emitieron antes de que el trámite existiera: recién ahora
            // se pueden adjuntar a sus Archivos.
            await adjuntarPactos(creado.idTramite);
            await adjuntarEscrito(creado.idTramite);
            setSuccess(`Trámite de Amparo por mora creado con éxito. ID: ${creado.idTramite}`);
            if (onSuccess) {
                onSuccess(creado);
            } else {
                navigate(`/amparo/tramites/${creado.idTramite}`);
            }
        } catch (err) {
            setError(err.response?.data || err.response?.data?.message || 'Error al crear el trámite.');
        } finally {
            setGuardando(false);
        }
    };

    const seccionTitulo = (icono, texto) => (
        <div style={{
            background: 'var(--primary-bg)',
            padding: '0.75rem 1.25rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--primary-border)', marginTop: '0.5rem'
        }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)', fontSize: 'var(--fs-texto-lg)' }}>
                <i className={`bi ${icono} me-2`}></i>{texto}
            </Typography>
        </div>
    );

    return (
        <div className="area-anses">
            <Box sx={{ background: 'var(--bg-page)', minHeight: '100vh', padding: 3 }}>
                <Box sx={{ maxWidth: 960, margin: 'auto', background: 'white', borderRadius: '16px', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
                    <PageHeader
                        variante="banner"
                        icono="shield-exclamation"
                        titulo="Iniciar Trámite de Amparo por Mora"
                        descripcion="Amparo por mora ante ANSES (art. 28 Ley 19.549): indicá por qué trámite se ampara"
                    />

                    <Box sx={{ padding: '2rem' }}>
                    <ModalRegistrarCliente open={modalClienteOpen} onClose={() => setModalClienteOpen(false)} onSuccess={handleRegistroExitoso} mostrarDialogoConfirmacion={false} />

                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            {seccionTitulo('bi-person-check', 'Seleccionar Cliente')}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Autocomplete
                                    id="buscar-cliente-amparo"
                                    options={opcionesCliente}
                                    getOptionLabel={(option) => `${apellidoNombre(option.apellido, option.nombre)} (${option.cuil})`}
                                    onInputChange={handleBusquedaCliente}
                                    onChange={handleSeleccionCliente}
                                    value={clienteSeleccionado}
                                    isOptionEqualToValue={(option, value) => option.cuil === value.cuil}
                                    noOptionsText="No se encontraron clientes"
                                    loading={buscando}
                                    loadingText="Buscando..."
                                    sx={{ flexGrow: 1 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Buscar cliente por CUIL, Apellido o Nombre"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {buscando ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Button variant="outlined" onClick={() => setModalClienteOpen(true)} sx={{ height: '56px', flexShrink: 0 }}>
                                    Registrar Cliente
                                </Button>
                            </Box>

                            {/* PACTO de honorarios, igual que en el resto de los altas: se emite
                                acá y el hook lo adjunta al trámite una vez creado, que además deja
                                cargados los haberes pactados y el check "Pacto OK" de la ficha. */}
                            <SeccionGenerarPacto cliente={clienteSeleccionado} onPactoGenerado={registrarPacto} />

                            {seccionTitulo('bi-signpost-split', 'Por qué trámite se ampara')}
                            <TextField
                                select
                                required
                                label="Trámite amparado"
                                name="subtipo"
                                value={form.subtipo}
                                onChange={handleChange}
                                fullWidth
                                helperText="Obligatorio. Es el renglón del tipo de trámite del escrito de amparo."
                            >
                                {subtipos.map(s => <MenuItem key={s.valor} value={s.valor}>{s.etiqueta}</MenuItem>)}
                            </TextField>

                            <TextField
                                select
                                label="Trámite de fondo del cliente (opcional)"
                                value={form.idTramiteFondo}
                                onChange={(e) => handleSeleccionFondo(e.target.value)}
                                fullWidth
                                disabled={!form.idCliente || cargandoTramites}
                                helperText={
                                    !form.idCliente
                                        ? 'Primero seleccioná un cliente.'
                                        : cargandoTramites
                                            ? 'Cargando los trámites del cliente...'
                                            : tramitesCliente.length === 0
                                                ? 'El cliente no tiene trámites previsionales cargados: el amparo se puede crear igual, sin vincular.'
                                                : 'Al vincularlo, el expediente y la fecha de ingreso se prellenan desde su turno ANSES, y la ficha de ese trámite muestra "Amparo iniciado".'
                                }
                            >
                                <MenuItem value=""><em>Sin vincular</em></MenuItem>
                                {tramitesCliente.map(t => (
                                    <MenuItem key={t.idTramite} value={String(t.idTramite)}>{etiquetaTramite(t)}</MenuItem>
                                ))}
                            </TextField>

                            {seccionTitulo('bi-folder2-open', 'Expediente demorado en ANSES')}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField label="N° de expediente" name="nroExpedienteAnses" value={form.nroExpedienteAnses}
                                    onChange={handleChange} fullWidth
                                    helperText="Si se vincula el trámite de fondo y queda vacío, se completa solo desde su turno ANSES." />
                                <TextField label="UDAI de gestión" name="udai" value={form.udai} onChange={handleChange} fullWidth />
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField label="Fecha de inicio del trámite en ANSES" name="fechaInicioTramiteAnses" type="date"
                                    value={form.fechaInicioTramiteAnses} onChange={handleChange} fullWidth
                                    InputLabelProps={{ shrink: true }} />
                                <TextField label="Tiempo comprometido" name="tiempoComprometido" value={form.tiempoComprometido}
                                    onChange={handleChange} fullWidth
                                    helperText="Renglón del escrito. Si queda vacío, sale el blanco a completar en Word." />
                            </Stack>

                            {seccionTitulo('bi-people', 'Titular del beneficio')}
                            <FormControlLabel
                                control={<Checkbox checked={form.reclamaPorTercero}
                                    onChange={(e) => setForm(prev => ({ ...prev, reclamaPorTercero: e.target.checked }))} />}
                                label="El cliente reclama por un tercero (el beneficio no es suyo)"
                            />
                            {form.reclamaPorTercero && (
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pl: 4 }}>
                                    <TextField label="Apellido y nombre del titular" name="titularNombre" value={form.titularNombre}
                                        onChange={handleChange} fullWidth required
                                        helperText="El escrito sale a nombre del titular; el domicilio sigue siendo el del cliente." />
                                    <TextField label="DNI / CUIL del titular" name="titularCuil" value={form.titularCuil}
                                        onChange={handleChange} fullWidth
                                        error={!esDniOCuilValido(form.titularCuil)}
                                        helperText={!esDniOCuilValido(form.titularCuil) ? 'Debe ser un DNI (7 u 8 dígitos) o un CUIL/CUIT (11 dígitos).' : ''} />
                                    <TextField label="Vínculo con el cliente" name="vinculoTitular" value={form.vinculoTitular}
                                        onChange={handleChange} fullWidth
                                        helperText="Ej: hijo, cónyuge, madre." />
                                </Stack>
                            )}

                            {seccionTitulo('bi-file-earmark-text', 'Datos adicionales')}
                            <TextField label="Etiqueta / referencia" name="etiqueta" value={form.etiqueta} onChange={handleChange} fullWidth />

                            <CampoFechaLiquidar
                                valor={form.fechaInicioEstimada}
                                onChange={(v) => setForm((f) => ({ ...f, fechaInicioEstimada: v }))}
                            />

                            <TextField label="Observaciones" name="comentarios" value={form.comentarios} onChange={handleChange} multiline rows={4} fullWidth
                                helperText="Particularidades del caso (lo que se le deja anotado al equipo)." />

                            {/* El escrito se puede emitir antes de dar de alta el trámite, como en
                                Amparo PNC: sirve para que el cliente se lo lleve firmado del mostrador. */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <BotonGenerarEscritoAmparo datos={datosEscrito} onGenerado={registrarEscrito} />
                            </Box>

                            {error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{String(error)}</Alert>}
                            {success && <Alert severity="success" sx={{ borderRadius: '10px' }}>{success}</Alert>}
                            {tieneDuplicado && (
                                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                    Este cliente ya tiene {cantidadDuplicados === 1 ? 'un amparo por mora' : `${cantidadDuplicados} amparos por mora`}. ¿Está seguro de que desea crear otro?
                                </Alert>
                            )}
                            {tieneDuplicado && (
                                <FormControlLabel
                                    control={<Checkbox checked={confirmarDuplicado} onChange={(e) => setConfirmarDuplicado(e.target.checked)} />}
                                    label="Confirmo que deseo continuar con un nuevo amparo por mora igualmente."
                                />
                            )}
                            {cargandoDuplicados && <Alert severity="info" sx={{ borderRadius: '10px' }}>Verificando amparos activos del cliente...</Alert>}

                            <Stack direction="row" spacing={2} sx={{ width: '100%', marginTop: '1rem' }}>
                                <Button fullWidth variant="outlined" size="large"
                                    onClick={() => onCancel ? onCancel() : navigate('/tramites')}
                                    sx={{ color: '#dc2626', border: '2px solid #fecaca', borderRadius: '10px', textTransform: 'none' }}>
                                    <i className="bi bi-x-circle me-2"></i>Cancelar
                                </Button>
                                <Button fullWidth type="submit" size="large" disabled={!form.idCliente || !form.subtipo || guardando}
                                    sx={{
                                        background: 'var(--primary)', color: 'white', borderRadius: '10px',
                                        textTransform: 'none', fontWeight: 600,
                                        '&:hover': { background: 'var(--primary-hover)' },
                                        '&:disabled': { background: '#e5e7eb', color: '#9ca3af' }
                                    }}>
                                    {guardando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <><i className="bi bi-check-circle me-2"></i>Iniciar Trámite</>}
                                </Button>
                            </Stack>
                        </Stack>
                    </form>
                </Box>
            </Box>
        </Box>
    </div>
);
}

/**
 * Traduce el trámite de fondo elegido al subtipo del amparo. Espejo (en el frontend) de
 * TipoTramiteResolver.subtipoSugerido para el caso AMPARO. Es solo una sugerencia: el operador la
 * ve en el selector y puede cambiarla.
 */
function sugerirSubtipo(tramite) {
    const nombre = String(tramite?.tipoTramite?.nombre || '').toUpperCase();
    const subtipoFondo = String(tramite?.subtipoTramite || '').toUpperCase();

    if (nombre.includes('RTI')) return 'RTI';
    if (nombre.includes('REAJUSTE')) return 'REAJUSTE';
    if (nombre.includes('PNC')) return 'PNC';
    if (nombre.includes('PENSION')) return subtipoFondo.includes('DERIVADA') ? 'PENSION_DERIVADA' : 'PENSION_DIRECTA';
    if (nombre.includes('JUBILAC')) return 'JUBILACION';
    return '';
}
